import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { LoadingBlock, ErrorState } from "@/components/ds";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { AlertTriangle, Download, Search } from "lucide-react";

const money = (n: any) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`);
const money2 = (n: any) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const COLORS = ["#f59e0b", "#3b82f6", "#10b981", "#a855f7"];

function KPI({ label, value, tone, testid }: any) {
  return (
    <div className="rounded-xl border border-white/5 bg-slate-900/60 p-4" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1 font-display text-2xl font-semibold ${tone || "text-white"}`}>{value}</p>
    </div>
  );
}
function Panel({ title, children, right }: any) {
  return (
    <div className="rounded-xl border border-white/5 bg-slate-900/60">
      <div className="flex items-center justify-between border-b border-white/5 px-5 py-3">
        <h2 className="text-sm font-semibold text-slate-200">{title}</h2>{right}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}
const H1 = ({ children }: any) => <h1 className="mb-6 text-2xl font-semibold text-white">{children}</h1>;

export function ExecOverview() {
  const ov = useQuery({ queryKey: ["exec-ov"], queryFn: () => api.execOverview() });
  const mix = useQuery({ queryKey: ["exec-mix"], queryFn: () => api.execSubscriptionMix() });
  const alerts = useQuery({ queryKey: ["exec-alerts"], queryFn: () => api.execAlerts() });
  if (ov.isLoading) return <LoadingBlock rows={6} />;
  if (ov.isError || !ov.data) return <ErrorState onRetry={ov.refetch} />;
  const d = ov.data;
  const pie = mix.data ? Object.entries(mix.data.mix).map(([k, v]: any) => ({ name: k, value: v.active })) : [];
  return (
    <div data-testid="executive-overview">
      <H1>Executive Command Center</H1>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <KPI label="Active Businesses" value={d.businesses.active.toLocaleString()} testid="kpi-active-biz" />
        <KPI label="MRR" value={money(d.revenue.mrr)} tone="text-emerald-400" testid="kpi-mrr" />
        <KPI label="ARR" value={money(d.revenue.arr)} tone="text-emerald-400" />
        <KPI label="Active Subs" value={d.subscriptions.active} tone="text-blue-400" />
        <KPI label="Active Reps" value={d.reps.active} />
        <KPI label="Outstanding Comp" value={money2(d.compensation.current_period_outstanding)} tone="text-amber-400" testid="kpi-outstanding" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <KPI label="Total Businesses" value={d.businesses.total.toLocaleString()} />
        <KPI label="New Today" value={d.businesses.new_today} />
        <KPI label="New 7d" value={d.businesses.new_7d} />
        <KPI label="New 30d" value={d.businesses.new_30d} />
        <KPI label="Past Due" value={d.subscriptions.past_due} tone="text-red-400" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Panel title="Subscription Mix">
          <div className="flex items-center gap-6">
            <ResponsiveContainer width={180} height={180}>
              <PieChart><Pie data={pie} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80}>
                {pie.map((_, i) => <Cell key={i} fill={COLORS[i % 4]} />)}</Pie><Tooltip /></PieChart>
            </ResponsiveContainer>
            <div className="text-sm">
              {mix.data && Object.entries(mix.data.mix).map(([k, v]: any, i) => (
                <div key={k} className="flex items-center gap-2 py-0.5">
                  <span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % 4] }} />
                  <span className="capitalize text-slate-300">{k}</span>
                  <span className="ml-auto text-slate-400">{v.active} · {v.percent}% · {money(v.mrr)}/mo</span>
                </div>
              ))}
              {mix.data && <p className="mt-2 text-xs text-amber-400">Growth Mix {mix.data.growth_mix_percent}% (target 70%, {mix.data.growth_mix_diff > 0 ? "+" : ""}{mix.data.growth_mix_diff}%)</p>}
            </div>
          </div>
        </Panel>
        <Panel title="Alerts & Exceptions">
          {alerts.data?.alerts?.length ? (
            <ul className="space-y-2 text-sm" data-testid="exec-alerts">
              {alerts.data.alerts.slice(0, 8).map((a: any, i: number) => (
                <li key={i} className="flex items-start gap-2 text-slate-300"><AlertTriangle className={`mt-0.5 h-4 w-4 ${a.severity === "warning" ? "text-amber-400" : "text-blue-400"}`} />{a.message}</li>
              ))}
            </ul>
          ) : <p className="py-4 text-center text-sm text-slate-500">No alerts.</p>}
        </Panel>
      </div>
    </div>
  );
}

export function ExecSalesTeam() {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["exec-team"], queryFn: () => api.execSalesTeam() });
  if (isLoading) return <LoadingBlock rows={6} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;
  const reps = [...(data.reps || [])].sort((a, b) => b.total_owed - a.total_owed);
  return (
    <div data-testid="executive-sales-team">
      <div className="mb-6 flex items-center justify-between"><H1>Sales Team</H1>
        <a href={api.execExportUrl("sales_reps")} className="flex items-center gap-1 rounded-lg border border-white/10 px-3 py-1.5 text-sm text-slate-300 hover:bg-white/5" data-testid="export-reps"><Download className="h-4 w-4" /> CSV</a>
      </div>
      <Panel title={`${reps.length} Representatives — current period`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500">{["Rep","Sales","Quota","Growth Mix","Base","Bonuses","Total Owed","Paid"].map((c) => <th key={c} className="px-2 py-2">{c}</th>)}</tr></thead>
            <tbody>
              {reps.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-slate-500">No reps.</td></tr>}
              {reps.map((r: any) => (
                <tr key={r.user_id} className="cursor-pointer border-t border-white/5 hover:bg-white/5" onClick={() => navigate(`/executive/reps/${r.user_id}`)} data-testid={`exec-rep-${r.user_id}`}>
                  <td className="px-2 py-2 font-medium text-amber-300">{r.rep_name}</td>
                  <td className="px-2 py-2">{r.total_paid_sales}/{r.quota}</td>
                  <td className="px-2 py-2">{r.quota_status.replace("_", " ")}</td>
                  <td className="px-2 py-2">{r.growth_mix_percent}%</td>
                  <td className="px-2 py-2">{money2(r.base_commission)}</td>
                  <td className="px-2 py-2">{money2(r.total_bonuses)}</td>
                  <td className="px-2 py-2 font-semibold text-white">{money2(r.total_owed)}</td>
                  <td className="px-2 py-2 text-slate-400">{money2(r.total_paid)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

export function ExecRepDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["exec-rep", userId], queryFn: () => api.execRepDetail(userId!) });
  if (isLoading) return <LoadingBlock rows={6} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;
  const l = data.lifetime, c = data.current_period;
  return (
    <div data-testid="executive-rep-detail">
      <button onClick={() => navigate("/executive/sales-team")} className="mb-3 text-sm text-slate-400 hover:text-white">← Sales Team</button>
      <H1>{data.profile.full_name}</H1>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KPI label="Lifetime Sales" value={l.total_sales} testid="rep-lifetime-sales" />
        <KPI label="Total Compensation" value={money2(l.total_compensation)} tone="text-amber-400" testid="rep-total-comp" />
        <KPI label="Total Paid" value={money2(l.total_paid)} tone="text-emerald-400" />
        <KPI label="Outstanding" value={money2(l.outstanding)} tone="text-amber-400" />
        <KPI label="Growth" value={l.by_plan.growth} /><KPI label="Pro" value={l.by_plan.pro} />
        <KPI label="Custom" value={l.by_plan.custom} /><KPI label="Starter" value={l.by_plan.starter} />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Panel title={`Current Period — ${c.total_paid_sales} sales`}>
          <dl className="space-y-1 text-sm text-slate-300">
            <Line k="Base Commission" v={money2(c.base_commission)} />
            <Line k="General Bonus" v={money2(c.general_performance_bonus)} />
            <Line k="Growth Accelerator" v={money2(c.growth_accelerator)} />
            <Line k="Pro Accelerator" v={money2(c.pro_accelerator)} />
            <Line k="Custom Accelerator" v={money2(c.custom_accelerator)} />
            <Line k="Total Owed" v={money2(c.total_owed)} bold />
          </dl>
        </Panel>
        <Panel title="Employment History">
          <dl className="space-y-1 text-sm text-slate-300">
            <Line k="Start Date" v={String(l.start_date).slice(0, 10)} />
            <Line k="Days Active" v={String(l.days_active)} />
            <Line k="Avg Monthly Sales" v={String(l.avg_monthly_sales)} />
            <Line k="Quota Periods Met" v={`${l.quota_periods_met} / ${l.quota_periods_total}`} />
            <Line k="Lifetime Base Commission" v={money2(l.base_commission)} />
            <Line k="Lifetime Bonuses" v={money2(l.total_bonuses)} />
          </dl>
        </Panel>
      </div>
      <div className="mt-6">
        <Panel title={`Sales acquired (${data.sales.length})`}>
          <div className="max-h-96 overflow-y-auto"><table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500">{["Date","Business","Plan","Collected","Commission","Status"].map((h) => <th key={h} className="px-2 py-1">{h}</th>)}</tr></thead>
            <tbody>{data.sales.map((s: any) => (
              <tr key={s.id} className="border-t border-white/5"><td className="px-2 py-1">{String(s.sale_date).slice(0, 10)}</td><td className="px-2 py-1">{s.business_name}</td><td className="px-2 py-1 capitalize">{s.plan}</td><td className="px-2 py-1">{money2(s.total_actual_collected)}</td><td className="px-2 py-1">{money2(s.commission_amount)}</td><td className="px-2 py-1 text-slate-400">{s.commission_status}</td></tr>
            ))}</tbody>
          </table></div>
        </Panel>
      </div>
    </div>
  );
}

export function ExecBusinesses() {
  const [q, setQ] = useState("");
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["exec-biz"], queryFn: () => api.execBusinesses({ limit: "100" }) });
  const search = useQuery({ queryKey: ["exec-search", q], queryFn: () => api.execSearch(q), enabled: q.length >= 2 });
  if (isLoading) return <LoadingBlock rows={6} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;
  const rows = q.length >= 2 && search.data ? search.data.businesses : data.businesses;
  return (
    <div data-testid="executive-businesses">
      <div className="mb-6 flex items-center justify-between"><H1>Businesses</H1>
        <a href={api.execExportUrl("businesses")} className="flex items-center gap-1 rounded-lg border border-white/10 px-3 py-1.5 text-sm text-slate-300 hover:bg-white/5" data-testid="export-biz"><Download className="h-4 w-4" /> CSV</a>
      </div>
      <div className="mb-4 flex items-center gap-2 rounded-lg border border-white/10 bg-slate-900/60 px-3 py-2">
        <Search className="h-4 w-4 text-slate-500" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search business, email, phone, org id…" className="w-full bg-transparent text-sm text-white outline-none" data-testid="exec-search-input" />
      </div>
      <Panel title={`${data.total.toLocaleString()} businesses`}>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-500">{["Business","Email","Industry","Status","Plan","Sub Status","Monthly"].map((h) => <th key={h} className="px-2 py-2">{h}</th>)}</tr></thead>
          <tbody>{rows.map((b: any) => (
            <tr key={b.organization_id} className="border-t border-white/5"><td className="px-2 py-1.5 text-white">{b.business_name || b.name}</td><td className="px-2 py-1.5 text-slate-400">{b.email || b.primary_email}</td><td className="px-2 py-1.5">{b.industry}</td><td className="px-2 py-1.5">{b.status}</td><td className="px-2 py-1.5 capitalize">{b.plan || "—"}</td><td className="px-2 py-1.5">{b.subscription_status || "—"}</td><td className="px-2 py-1.5">{b.monthly_price ? money(b.monthly_price) : "—"}</td></tr>
          ))}</tbody>
        </table></div>
      </Panel>
    </div>
  );
}

export function ExecAnalytics() {
  const ind = useQuery({ queryKey: ["exec-ind"], queryFn: () => api.execIndustry() });
  const geo = useQuery({ queryKey: ["exec-geo"], queryFn: () => api.execGeo() });
  const mix = useQuery({ queryKey: ["exec-mix2"], queryFn: () => api.execSubscriptionMix() });
  return (
    <div data-testid="executive-analytics">
      <H1>Analytics</H1>
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Businesses by Industry">
          {ind.data ? <ResponsiveContainer width="100%" height={260}>
            <BarChart data={ind.data.industries.slice(0, 8)}><XAxis dataKey="industry" tick={{ fill: "#94a3b8", fontSize: 11 }} /><YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} /><Tooltip /><Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer> : <LoadingBlock rows={4} />}
        </Panel>
        <Panel title="Businesses by State">
          {geo.data ? (geo.data.by_state.length ? <ResponsiveContainer width="100%" height={260}>
            <BarChart data={geo.data.by_state.slice(0, 10)}><XAxis dataKey="state" tick={{ fill: "#94a3b8", fontSize: 11 }} /><YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} /><Tooltip /><Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer> : <p className="py-8 text-center text-sm text-slate-500">No location data captured yet.</p>) : <LoadingBlock rows={4} />}
        </Panel>
      </div>
      {mix.data && <div className="mt-6"><Panel title="Subscription Distribution">
        <table className="w-full text-sm"><thead><tr className="text-left text-xs text-slate-500"><th className="px-2 py-2">Plan</th><th>Active</th><th>%</th><th>MRR</th><th>ARR</th></tr></thead>
          <tbody>{Object.entries(mix.data.mix).map(([k, v]: any) => (<tr key={k} className="border-t border-white/5"><td className="px-2 py-1.5 capitalize text-white">{k}</td><td className="px-2 py-1.5">{v.active}</td><td className="px-2 py-1.5">{v.percent}%</td><td className="px-2 py-1.5">{money(v.mrr)}</td><td className="px-2 py-1.5">{money(v.arr)}</td></tr>))}</tbody>
        </table>
      </Panel></div>}
    </div>
  );
}

export function ExecCompensation() {
  const liab = useQuery({ queryKey: ["exec-liab"], queryFn: () => api.execCompensationLiability() });
  const rec = useQuery({ queryKey: ["exec-rec"], queryFn: () => api.execReconciliation() });
  if (liab.isLoading) return <LoadingBlock rows={5} />;
  if (liab.isError || !liab.data) return <ErrorState onRetry={liab.refetch} />;
  const l = liab.data;
  return (
    <div data-testid="executive-compensation">
      <H1>Sales Compensation Liability</H1>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <KPI label="Pending Commissions" value={money2(l.pending_commissions)} />
        <KPI label="Earned Commissions" value={money2(l.earned_commissions)} tone="text-blue-400" testid="liab-earned" />
        <KPI label="Paid Commissions" value={money2(l.paid_commissions)} tone="text-emerald-400" />
        <KPI label="Current Period Owed" value={money2(l.current_period_owed)} tone="text-amber-400" testid="liab-owed" />
        <KPI label="Current Outstanding" value={money2(l.current_period_outstanding)} tone="text-amber-400" />
      </div>
      {rec.data && <div className="mt-6"><Panel title="Stripe ↔ ResFil Reconciliation">
        <div className="grid grid-cols-2 gap-4 text-sm text-slate-300 lg:grid-cols-4">
          <div>Total sales: <b className="text-white">{rec.data.total_sales}</b></div>
          <div>Missing Stripe ref: <b className="text-amber-400">{rec.data.sales_without_stripe_ref}</b></div>
          <div>Refunds affecting comm: <b className="text-amber-400">{rec.data.refunds_affecting_commission}</b></div>
          <div>Disputes: <b className="text-red-400">{rec.data.disputes}</b></div>
        </div>
      </Panel></div>}
    </div>
  );
}

function Line({ k, v, bold }: any) {
  return <div className="flex justify-between"><dt className="text-slate-400">{k}</dt><dd className={bold ? "font-semibold text-white" : "text-slate-200"}>{v}</dd></div>;
}

// ---------------------------------- Module 52: Bonus Visibility ----------------------------------
const STATUS_STYLES: Record<string, string> = {
  paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  partial: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  none: "bg-slate-700/40 text-slate-400 border-white/10",
};
function StatusTag({ status }: { status: string }) {
  const label = status === "none" ? "No bonus" : status.charAt(0).toUpperCase() + status.slice(1);
  return <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLES[status] || STATUS_STYLES.none}`} data-testid={`bonus-status-${status}`}>{label}</span>;
}

const BONUS_CATS = [
  { key: "general", label: "General Monthly Performance", color: "#f59e0b", schedule: "general_performance" },
  { key: "growth", label: "Growth Accelerator", color: "#3b82f6", schedule: "growth_accelerator" },
  { key: "pro", label: "Pro Accelerator", color: "#10b981", schedule: "pro_accelerator" },
  { key: "custom", label: "Custom Accelerator", color: "#a855f7", schedule: "custom_accelerator" },
];

// The same four ladders for annual sales, with their own thresholds. A rep who reaches tiers in
// both earns both, so the two schedules are shown side by side rather than merged.
const ANNUAL_BONUS_CATS = [
  { key: "annual_performance", label: "Annual Sales Performance", color: "#f59e0b", schedule: "annual_performance" },
  { key: "growth", label: "Growth Annual Accelerator", color: "#3b82f6", schedule: "growth_accelerator" },
  { key: "pro", label: "Pro Annual Accelerator", color: "#10b981", schedule: "pro_accelerator" },
  { key: "custom", label: "Custom Annual Accelerator", color: "#a855f7", schedule: "custom_accelerator" },
];

function ScheduleGrid({ cats, schedule, testPrefix }: any) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {cats.map((cat: any) => (
        <div key={cat.key} data-testid={`${testPrefix}-${cat.key}`}>
          <p className="mb-1.5 flex items-center gap-2 text-xs font-semibold text-slate-200">
            <span className="h-2 w-2 rounded-full" style={{ background: cat.color }} />{cat.label}
          </p>
          <ul className="space-y-0.5 text-xs text-slate-400">
            {(schedule?.[cat.schedule] || []).map((tier: any, i: number) => (
              <li key={i} className="flex justify-between"><span>{tier.range} sales</span><span className="font-medium text-slate-300">{money(tier.amount)}</span></li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function TierProgress({ label, color, p }: any) {
  const pct = p.maxed
    ? 100
    : p.next_threshold
      ? Math.min(100, Math.round((p.count / p.next_threshold) * 100))
      : 0;
  return (
    <div className="rounded-lg border border-white/5 bg-slate-900/40 p-3" data-testid={`bonus-progress-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="font-medium text-slate-200">{label}</span>
        <span className="font-semibold" style={{ color }}>{money2(p.current_amount)}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <p className="mt-1 text-[11px] text-slate-400">
        {p.count} qualifying{p.maxed ? " · top tier reached" : p.next_threshold ? ` · ${p.sales_to_next} more → ${money(p.next_amount)} at ${p.next_threshold}` : " · no tier yet"}
      </p>
    </div>
  );
}

function RepBonusDrilldown({ rep }: any) {
  const c = rep.current_period;
  const lt = rep.lifetime;
  return (
    <div className="space-y-6" data-testid={`bonus-drilldown-${rep.user_id}`}>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KPI label="Bonuses Earned (lifetime)" value={money2(lt.total_bonuses_earned)} tone="text-amber-400" testid="rep-bonus-earned" />
        <KPI label="Bonuses Paid" value={money2(lt.total_bonuses_paid)} tone="text-emerald-400" />
        <KPI label="Bonuses Pending" value={money2(lt.total_bonuses_pending)} tone="text-amber-300" />
        <KPI label="Total Compensation" value={money2(lt.total_compensation)} tone="text-white" testid="rep-total-compensation" />
      </div>

      {c && (
        <Panel title={`Current Performance Period — ${c.total_sales}/${c.quota} sales · ${c.days_remaining} days left`}
          right={<StatusTag status={c.payout_status} />}>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
            Monthly sales · {c.monthly_sales ?? 0}
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <TierProgress label="General Monthly Performance" color="#f59e0b" p={c.progress.general} />
            <TierProgress label="Growth Accelerator" color="#3b82f6" p={c.progress.growth} />
            <TierProgress label="Pro Accelerator" color="#10b981" p={c.progress.pro} />
            <TierProgress label="Custom Accelerator" color="#a855f7" p={c.progress.custom} />
          </div>
          {c.annual_progress && (
            <>
              <p className="mb-2 mt-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                Annual sales · {c.annual_sales ?? 0}
              </p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <TierProgress label="Annual Sales Performance" color="#f59e0b" p={c.annual_progress.annual_performance} />
                <TierProgress label="Growth Annual Accelerator" color="#3b82f6" p={c.annual_progress.growth} />
                <TierProgress label="Pro Annual Accelerator" color="#10b981" p={c.annual_progress.pro} />
                <TierProgress label="Custom Annual Accelerator" color="#a855f7" p={c.annual_progress.custom} />
              </div>
            </>
          )}
          <dl className="mt-5 space-y-1 border-t border-white/5 pt-4 text-sm text-slate-300">
            <Line k="Base Commission" v={money2(c.base_commission)} />
            {(c.held_commission ?? 0) > 0 && <Line k="Annual commission held (refund window)" v={money2(c.held_commission)} />}
            <Line k="Total Bonuses (earned this period)" v={money2(c.total_bonuses)} />
            <Line k="Total Compensation (this period)" v={money2(c.base_commission + c.total_bonuses)} bold />
            <Line k="Paid" v={money2(c.total_paid)} />
            <Line k="Outstanding" v={money2(c.outstanding)} />
          </dl>
        </Panel>
      )}

      <Panel title="Bonus History — completed periods">
        {rep.history.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500">{["Period","Sales","General","Growth","Pro","Custom","Total Bonus","Status"].map((h) => <th key={h} className="px-2 py-2">{h}</th>)}</tr></thead>
              <tbody>
                {rep.history.map((h: any) => (
                  <tr key={h.period_index} className="border-t border-white/5" data-testid={`bonus-history-${rep.user_id}-${h.period_index}`}>
                    <td className="px-2 py-2 text-slate-300">{String(h.period_start).slice(0, 10)} → {String(h.period_end).slice(0, 10)}</td>
                    <td className="px-2 py-2">{h.total_sales}</td>
                    <td className="px-2 py-2">{money2(h.general_performance_bonus)}</td>
                    <td className="px-2 py-2">{money2(h.growth_accelerator)}</td>
                    <td className="px-2 py-2">{money2(h.pro_accelerator)}</td>
                    <td className="px-2 py-2">{money2(h.custom_accelerator)}</td>
                    <td className="px-2 py-2 font-semibold text-amber-300">{money2(h.total_bonuses)}</td>
                    <td className="px-2 py-2"><StatusTag status={h.payout_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="py-6 text-center text-sm text-slate-500">No completed periods yet — this rep is in their first performance period.</p>}
      </Panel>
    </div>
  );
}

export function ExecBonuses() {
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["exec-bonuses"], queryFn: () => api.execBonuses() });
  const [selected, setSelected] = useState<string | null>(null);
  if (isLoading) return <LoadingBlock rows={6} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;
  const reps: any[] = data.reps || [];
  const t = data.totals;
  const sched = data.schedule;
  const sel = reps.find((r) => r.user_id === selected) || (reps.length === 1 ? reps[0] : null);

  return (
    <div data-testid="executive-bonuses">
      <H1>Bonuses & Accelerators</H1>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KPI label="Total Bonuses Earned" value={money2(t.earned)} tone="text-amber-400" testid="bonus-total-earned" />
        <KPI label="Bonuses Paid" value={money2(t.paid)} tone="text-emerald-400" testid="bonus-total-paid" />
        <KPI label="Bonuses Pending" value={money2(t.pending)} tone="text-amber-300" testid="bonus-total-pending" />
        <KPI label="Total Compensation" value={money2(t.comp)} tone="text-white" testid="bonus-total-comp" />
      </div>
      <p className="mt-5 text-xs font-semibold uppercase tracking-widest text-slate-500">Bonuses for monthly sales</p>
      <div className="mt-2 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KPI label="General Performance" value={money2(t.general)} />
        <KPI label="Growth Accelerator" value={money2(t.growth)} />
        <KPI label="Pro Accelerator" value={money2(t.pro)} />
        <KPI label="Custom Accelerator" value={money2(t.custom)} />
      </div>
      <p className="mt-5 text-xs font-semibold uppercase tracking-widest text-slate-500">Bonuses for annual sales</p>
      <div className="mt-2 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KPI label="Annual Sales Performance" value={money2(t.annual_performance)} testid="bonus-annual-performance" />
        <KPI label="Growth Annual Accelerator" value={money2(t.annual_growth)} testid="bonus-annual-growth" />
        <KPI label="Pro Annual Accelerator" value={money2(t.annual_pro)} testid="bonus-annual-pro" />
        <KPI label="Custom Annual Accelerator" value={money2(t.annual_custom)} testid="bonus-annual-custom" />
      </div>
      {t.held > 0 && (
        <p className="mt-3 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-xs text-amber-200/90" data-testid="bonus-held-note">
          {money2(t.held)} of annual commission is held until the 30-day refund window closes on those sales. It is not payable yet.
        </p>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Panel title="Bonus Schedule (highest tier applies — no stacking)">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Monthly sales</p>
          <ScheduleGrid cats={BONUS_CATS} schedule={sched} testPrefix="bonus-schedule" />
          <p className="mb-2 mt-5 border-t border-white/5 pt-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
            Annual sales · {sched?.annual_rate_percent ?? 15}% commission, held {sched?.annual_hold_days ?? 31} days
          </p>
          <ScheduleGrid cats={ANNUAL_BONUS_CATS} schedule={sched?.annual} testPrefix="bonus-annual-schedule" />
          <p className="mt-4 border-t border-white/5 pt-3 text-[11px] text-slate-500">
            {data.payout_cadence} A rep who reaches tiers on both monthly and annual sales earns both.
          </p>
        </Panel>

        <Panel title="Bonus Ledger — current period by rep">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500">{["Rep","Sales","Total Bonus","Status"].map((h) => <th key={h} className="px-2 py-2">{h}</th>)}</tr></thead>
              <tbody>
                {reps.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-500">No reps.</td></tr>}
                {reps.map((r) => {
                  const c = r.current_period;
                  const active = sel?.user_id === r.user_id;
                  return (
                    <tr key={r.user_id} onClick={() => setSelected(r.user_id)}
                      className={`cursor-pointer border-t border-white/5 hover:bg-white/5 ${active ? "bg-amber-500/10" : ""}`}
                      data-testid={`bonus-ledger-row-${r.user_id}`}>
                      <td className="px-2 py-2 font-medium text-amber-300">{r.rep_name}</td>
                      <td className="px-2 py-2">{c ? c.total_sales : 0}</td>
                      <td className="px-2 py-2 font-semibold text-white">{money2(c ? c.total_bonuses : 0)}</td>
                      <td className="px-2 py-2">{c ? <StatusTag status={c.payout_status} /> : <StatusTag status="none" />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {reps.length > 1 && <p className="mt-3 text-[11px] text-slate-500">Select a rep to view their bonus drilldown below.</p>}
        </Panel>
      </div>

      {sel && (
        <div className="mt-8">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-white">
            <span className="text-amber-400">{sel.rep_name}</span>
            <span className="text-sm font-normal text-slate-400">— bonus drilldown</span>
          </h2>
          <RepBonusDrilldown rep={sel} />
        </div>
      )}
    </div>
  );
}
